import { useCallback, useEffect, useMemo, useState } from 'react'
import { isOtpSendRefusal } from '@/shared/domain'
import {
  clearDeadline,
  otpSendCooldownKey,
  readDeadline,
  readDeadlineOfKind,
  subscribeDeadlines,
  writeDeadline,
  type OtpSendCooldownChannel,
  type OtpSendDeadlineKind,
} from './otp-send-cooldown-storage'

export type OtpSendCooldownTarget = {
  channel: OtpSendCooldownChannel
  identifier: string
}

export type UseOtpSendCooldownResult = {
  secondsLeft: number
  isCoolingDown: boolean
  applyRefusal: (err: unknown, forTarget?: OtpSendCooldownTarget) => boolean
  recordSend: (forTarget?: OtpSendCooldownTarget) => void
  clearCooldown: (forTarget?: OtpSendCooldownTarget) => void
}

export const POST_SEND_COOLDOWN_SECONDS = 60

const TICK_MS = 1000

type MemoryDeadlines = Record<OtpSendDeadlineKind, Record<string, number>>

const EMPTY_MEMORY: MemoryDeadlines = { refusal: {}, send: {} }

function hasIdentifier(target: OtpSendCooldownTarget | null): target is OtpSendCooldownTarget {
  return target !== null && target.identifier.trim() !== ''
}

export function useOtpSendCooldown(target: OtpSendCooldownTarget | null): UseOtpSendCooldownResult {
  const channel = target?.channel ?? null
  const identifier = target?.identifier ?? ''
  const key = channel !== null && identifier.trim() !== '' ? otpSendCooldownKey(channel, identifier) : null

  const [memoryDeadlines, setMemoryDeadlines] = useState<MemoryDeadlines>(EMPTY_MEMORY)
  const [now, setNow] = useState<number>(() => Date.now())
  const [storeVersion, setStoreVersion] = useState<number>(0)

  useEffect(
    () =>
      subscribeDeadlines(() => {
        setNow(Date.now())
        setStoreVersion((v) => v + 1)
      }),
    [],
  )

  const persistedDeadline = useMemo(
    () => (channel !== null && key !== null && storeVersion >= 0 ? readDeadline(channel, identifier) : null),
    [channel, identifier, key, storeVersion],
  )

  const refusalMemoryDeadline = key !== null ? (memoryDeadlines.refusal[key] ?? null) : null
  const sendMemoryDeadline = key !== null ? (memoryDeadlines.send[key] ?? null) : null
  const deadline = Math.max(persistedDeadline ?? 0, refusalMemoryDeadline ?? 0, sendMemoryDeadline ?? 0)

  useEffect(() => {
    if (deadline <= 0) return
    setNow(Date.now())
    const id = setInterval(() => {
      const current = Date.now()
      setNow(current)
      if (current >= deadline) clearInterval(id)
    }, TICK_MS)
    return () => clearInterval(id)
  }, [deadline])

  const startCooldown = useCallback(
    (
      forTarget: OtpSendCooldownTarget | undefined,
      seconds: number,
      kind: OtpSendDeadlineKind,
    ): void => {
      const effective = forTarget ?? (channel !== null ? { channel, identifier } : null)
      if (!hasIdentifier(effective)) return
      const current = Date.now()
      const targetKey = otpSendCooldownKey(effective.channel, effective.identifier)
      const nextDeadline = Math.max(
        current + seconds * TICK_MS,
        readDeadlineOfKind(kind, effective.channel, effective.identifier) ?? 0,
      )
      setNow(current)
      setMemoryDeadlines((prev) => ({
        ...prev,
        [kind]: { ...prev[kind], [targetKey]: nextDeadline },
      }))
      writeDeadline(effective.channel, effective.identifier, nextDeadline, kind)
    },
    [channel, identifier],
  )

  const applyRefusal = useCallback(
    (err: unknown, forTarget?: OtpSendCooldownTarget): boolean => {
      if (!isOtpSendRefusal(err)) return false
      startCooldown(forTarget, err.retryAfterSeconds, 'refusal')
      return true
    },
    [startCooldown],
  )

  const recordSend = useCallback(
    (forTarget?: OtpSendCooldownTarget): void => {
      startCooldown(forTarget, POST_SEND_COOLDOWN_SECONDS, 'send')
    },
    [startCooldown],
  )

  const clearCooldown = useCallback(
    (forTarget?: OtpSendCooldownTarget): void => {
      const effective = forTarget ?? (channel !== null ? { channel, identifier } : null)
      if (!hasIdentifier(effective)) return
      const targetKey = otpSendCooldownKey(effective.channel, effective.identifier)
      setMemoryDeadlines((prev) => {
        if (!(targetKey in prev.send)) return prev
        const nextSend = { ...prev.send }
        delete nextSend[targetKey]
        return { ...prev, send: nextSend }
      })
      clearDeadline(effective.channel, effective.identifier, 'send')
    },
    [channel, identifier],
  )

  const secondsLeft = deadline > 0 ? Math.max(0, Math.ceil((deadline - now) / TICK_MS)) : 0

  return { secondsLeft, isCoolingDown: secondsLeft > 0, applyRefusal, recordSend, clearCooldown }
}
